/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { Redirect } from 'react-router-dom';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Box, Button, Heading, Loader } from '@opendoor/bricks/core';
import { trackEvent } from '@opendoor/shared-fe/build/analytics';

import MarketOptions, { MarketOptionsProps } from '../../components/MarketOptions';
import PartnerOptions, { PartnerOptionsProps } from '../../components/PartnerOptions';
import { UNSAFE_ISingleLineInputProps } from '../../components/UNSAFE_SingleLineInput';
import history from '../../config/history';
import { ERROR_PAGE_ROUTE } from '../routes';

type SignUpStartProps = {
  location?: {
    state?: {
      email?: string;
      partnershipType?: string;
      role?: string;
      partnerIdentifier?: string;
    };
  };
};

type SignUpStartState = {
  marketIdentifier?: string;
  partnerIdentifier?: string;
  otherPartnerIdentifier?: string;
  otherMarketIdentifier?: string;
  partnerOptionsLoading?: boolean;
};
class SignUpStart extends React.Component<SignUpStartProps, SignUpStartState> {
  public constructor(props: SignUpStartProps) {
    super(props);

    const partnerIdentifier =
      props.location && props.location.state && props.location.state.partnerIdentifier
        ? props.location.state.partnerIdentifier
        : '';
    this.state = {
      marketIdentifier: '',
      partnerIdentifier,
      partnerOptionsLoading: true,
    };
  }

  public componentDidMount() {
    trackEvent.apply(null, ['partner-sign-up-flow', 'start-sign-up', 'page']);

    const userAgent = window.navigator.userAgent;
    const idx = userAgent.indexOf('Edge/');
    if (idx > -1) {
      const version = userAgent.substring(idx + 5, idx + 7);
      if (parseInt(version, 10) < 79) {
        showToast(
          'info',
          'We do not support this version of Microsoft Edge. ' +
            'Please update your browser <a href="https://www.microsoft.com/en-us/edge" target="_blank">here</a>, ' +
            'or switch to Chrome or Firefox',
          'partnership-dashboard-fe-unsupported-edge-version-info-toast',
          { toastOptions: { autoClose: false } },
        );
      }
    }
  }

  public render() {
    if (!this.props.location!.state) {
      return <Redirect to={ERROR_PAGE_ROUTE} />;
    }
    const { email, partnershipType, role } = this.props.location!.state!;
    if (!(email && partnershipType && role)) {
      return <Redirect to={ERROR_PAGE_ROUTE} />;
    }

    let companyTypeText = 'company';
    switch (partnershipType) {
      case 'HOME_BUILDER':
        companyTypeText = 'home builder';
        break;
      default:
        break;
    }
    const {
      marketIdentifier,
      partnerIdentifier,
      otherPartnerIdentifier,
      otherMarketIdentifier,
      partnerOptionsLoading,
    } = this.state;
    return (
      <form onSubmit={this.handleSubmit}>
        <Box padding={[5, 5, 0]} margin="auto" maxWidth="580px">
          <Heading
            size="UNSAFE_h2"
            as="h2"
            fontSize={['s3', 'UNSAFE_h2', 's5']}
            mb={7}
            textAlign={['initial', 'center']}
          >
            Tell us about yourself
          </Heading>
          {partnerOptionsLoading && <Loader />}
          <PartnerOptions
            partnerIdentifier={partnerIdentifier}
            partnershipType={partnershipType}
            onChange={this.handlePartnerIdentifierChange}
            title={`Which ${companyTypeText} do you work for?`}
            placeholder="Search or Select"
            otherPartnerIdentifier={otherPartnerIdentifier}
            onOtherChange={this.handleOtherPartnerIdentifierChange}
            showLoader={this.handlePartnerOptionsLoading}
          />
          <MarketOptions
            marketIdentifier={marketIdentifier}
            onChange={this.handleMarketIdentifierChange}
            partnerIdentifier={partnerIdentifier || 'none'}
            title="What city do you work in?"
            placeholder="Choose your market"
            isDisabled={!partnerIdentifier}
            otherMarketIdentifier={otherMarketIdentifier}
            onOtherChange={this.handleOtherMarketIdentifierChange}
            partnerOptionsLoading={!!partnerOptionsLoading}
          />
          <Button
            disabled={!this.partnerAndMarketSubmitted()}
            onClick={this.handleSubmit}
            analyticsName="partnership-dashboard-fe-sign-up-continue"
            aria-label=""
          >
            Continue
          </Button>
        </Box>
      </form>
    );
  }

  public partnerAndMarketSubmitted = () => {
    const { marketIdentifier, partnerIdentifier, otherMarketIdentifier, otherPartnerIdentifier } =
      this.state!;
    const partnerSubmitted =
      partnerIdentifier === 'other_builder' ? !!otherPartnerIdentifier : !!partnerIdentifier;
    const marketSubmitted =
      marketIdentifier === 'other' ? !!otherMarketIdentifier : !!marketIdentifier;
    return partnerSubmitted && marketSubmitted;
  };

  public handlePartnerOptionsLoading = (isLoading: boolean) => {
    this.setState({
      partnerOptionsLoading: isLoading,
    });
  };

  public handlePartnerIdentifierChange: PartnerOptionsProps['onChange'] = (value) => {
    value &&
      this.setState({
        partnerIdentifier: value.toString(),
      });
  };

  public handleMarketIdentifierChange: MarketOptionsProps['onChange'] = (value) => {
    value &&
      this.setState({
        marketIdentifier: value.toString(),
      });
  };

  public handleOtherPartnerIdentifierChange: UNSAFE_ISingleLineInputProps['onChange'] = (
    _,
    value,
  ) => {
    if (value) {
      this.setState({
        otherPartnerIdentifier: value.toString(),
      });
    }
  };

  public handleOtherMarketIdentifierChange: UNSAFE_ISingleLineInputProps['onChange'] = (
    _,
    value,
  ) => {
    if (value) {
      this.setState({
        otherMarketIdentifier: value.toString(),
      });
    }
  };

  public handleSubmit = () => {
    const { marketIdentifier, partnerIdentifier, otherMarketIdentifier, otherPartnerIdentifier } =
      this.state!;
    const { email, role } = this.props.location!.state!;

    trackEvent.apply(null, [
      'partner-sign-up-flow',
      'account-created',
      partnerIdentifier,
      {
        email,
        role,
      },
    ]);

    history.push('/sign-up/account', {
      email,
      marketIdentifier,
      partnerIdentifier,
      otherMarketIdentifier,
      otherPartnerIdentifier,
      role,
    });
  };
}

export default SignUpStart;
